/**
 * `ModuleMobil` — the "Mobilversion" hero module. CHROMELESS: instead of the app
 * top-bar + sidebar chrome it shows three iPhones side-by-side (Inbox list,
 * Dokumente grid, Team-Chat), each captioned in mono. A 1:1 port of the
 * prototype's `isMobil` block (Hausverwaltung Hero.dc.html, lines 93–259). The
 * device shells come from the shared `IPhoneFrame` primitive; the screen
 * interiors live in `module-mobil-phones.tsx`.
 */
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { IPhoneFrame } from "../../hero-iphone";
import { H } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import { PhoneChat, PhoneDocs, PhoneInbox } from "./module-mobil-phones";
import { StoreBadges } from "./store-logos";

const PHONE_WIDTH = 262;
const PHONE_HEIGHT = 510;

/** A single phone column: the framed screen plus its mono caption. */
function PhoneColumn(props: {
  readonly caption: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 13,
      }}
    >
      <IPhoneFrame width={PHONE_WIDTH} height={PHONE_HEIGHT}>
        {props.children}
      </IPhoneFrame>
      <span
        style={{
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: H.inkFaint,
          fontWeight: 500,
        }}
      >
        {props.caption}
      </span>
    </div>
  );
}

/** Store badges for the staff app, centered on the stage's bottom edge. */
export function MobilStoreOverlay(): ReactNode {
  return (
    <StoreBadges
      app="staff"
      style={{
        position: "absolute",
        left: "50%",
        bottom: -20,
        transform: "translateX(-50%)",
        zIndex: 35,
      }}
    />
  );
}

export function ModuleMobil(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleMobil");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 54,
        padding: "30px 24px",
        boxSizing: "border-box",
      }}
    >
      <PhoneColumn caption="Inbox">
        <PhoneInbox />
      </PhoneColumn>
      <PhoneColumn caption={t("dokumente")}>
        <PhoneDocs />
      </PhoneColumn>
      <PhoneColumn caption="Team-Chat">
        <PhoneChat />
      </PhoneColumn>
    </div>
  );
}
